import { ErrorToast, reportError, SuccessToast } from '@vgrubs/components';
import { useRef, useState } from 'react';
import {
  deleteMenuAiDraft,
  getMenuAiDraft,
  improveMenuAi,
  refineMenuAiItem,
  saveMenuAiDraft,
  undoMenuAiItem,
} from '../../../api/menuAi.api';
import {
  ImproveMenuAiDto,
  MenuAiDraftData,
  MenuAiDraftStatus,
  RefineMenuAiItemDto,
} from '../../../types/api/menuAi.types';
import useMenuAiSocket from '../MenuAiSocket';

/**
 * Owns all Menu-AI improve state and wires the socket to the REST endpoints.
 * Mirrors the REST-trigger + socket-progress + guard-by-id pattern used in
 * Orders.tsx (`activeDraftIdRef` ~ `currentExportIdRef`): the `/menu` socket
 * room is keyed by location/brand, so payloads for a superseded draft must be
 * ignored.
 *
 * The draft is shared server-side per brand across all admins, so every OPEN of
 * the modal calls `discardDraft()` — an UNCONDITIONAL brand-scoped delete (no
 * client-known id) — to wipe it and start from the clean menu. This stops
 * improvements from silently stacking, including for a different admin or a
 * user who abandoned via refresh/close-tab instead of Cancel. State is also
 * cleared here when the draft is consumed (`saved`).
 */
export const useMenuAiImprove = ({
  locationId,
  brandId,
  menuId,
  onSaved,
}: {
  locationId: string;
  brandId: string;
  /** The draft belongs to one menu; saving writes into that menu. */
  menuId: number | null;
  onSaved: () => void;
}) => {
  const [draftId, setDraftId] = useState<string | null>(null);
  const [status, setStatus] = useState<MenuAiDraftStatus | null>(null);
  const [progress, setProgress] = useState(0);
  const [draftData, setDraftData] = useState<MenuAiDraftData | null>(null);
  const [loading, setLoading] = useState(false);
  // True while deleting a previous draft on (re)open — drives a blocking loader.
  const [discarding, setDiscarding] = useState(false);

  const activeDraftIdRef = useRef<string | null>(null);

  const { observeMenu } = useMenuAiSocket({
    onProgress: (payload) => {
      if (payload.draftId !== activeDraftIdRef.current) return;
      setProgress((prev) =>
        payload.progress > prev ? payload.progress : prev
      );
    },
    onFinished: async (payload) => {
      if (payload.draftId !== activeDraftIdRef.current) return;

      if (payload.status === 'ready') {
        try {
          const { data } = await getMenuAiDraft(locationId, payload.draftId);
          setDraftData(data.data.draftData);
          setProgress(100);
          setStatus('ready');
        } catch (error) {
          setStatus('failed');
          ErrorToast('Failed to load the AI menu draft.');
        }
      } else if (payload.status === 'saved') {
        // The draft is consumed — clear everything HERE (not on modal close),
        // which also covers a late `saved` arriving after the modal was
        // closed. `onSaved` closes the modal in the same batch, so status
        // going straight to null never shows an inconsistent frame.
        observeMenu(locationId, brandId, false);
        activeDraftIdRef.current = null;
        setDraftId(null);
        setDraftData(null);
        setProgress(0);
        setStatus(null);
        SuccessToast('Menu successfully updated with AI.');
        onSaved();
      } else {
        setStatus('failed');
        ErrorToast(payload.error || 'AI menu improvement failed.');
      }
    },
  });

  const startImprove = async (dto: ImproveMenuAiDto) => {
    // Flip to the busy state SYNCHRONOUSLY (before the async POST) so the footer
    // button disables and the progress loader shows on the same click. Otherwise
    // `status` only became 'pending' after the round-trip, leaving a window where
    // the enabled button could be clicked several times → several improve calls.
    setProgress(0);
    setStatus('pending');
    try {
      setLoading(true);
      // `draftData` is intentionally KEPT during a re-run — the backend builds
      // the new draft on top of the existing ready one, so the preview stays
      // visible under the working glow until the new result replaces it.
      const { data } = await improveMenuAi(locationId, brandId, dto, menuId);
      const id = data.data.draftId;
      setDraftId(id);
      activeDraftIdRef.current = id;
      observeMenu(locationId, brandId, true);
    } catch (error) {
      setStatus('failed');
      ErrorToast('Failed to start AI menu improvement.');
    } finally {
      setLoading(false);
    }
  };

  const refineItem = async (dto: RefineMenuAiItemDto) => {
    if (!draftId) return;
    try {
      setLoading(true);
      const { data } = await refineMenuAiItem(locationId, draftId, dto);
      setDraftData(data.data);
      SuccessToast('Item refined.');
    } catch (error) {
      ErrorToast('Failed to refine the item.');
    } finally {
      setLoading(false);
    }
  };

  const undoItem = async (itemId: number) => {
    if (!draftId) return;
    try {
      setLoading(true);
      const { data } = await undoMenuAiItem(locationId, draftId, itemId);
      setDraftData(data.data);
      SuccessToast('Item restored to its original menu.');
    } catch (error) {
      reportError(error, {
        context: 'undoMenuAiItem',
        fallbackMessage: 'Failed to revert the item.',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    if (!draftId) return;
    try {
      setLoading(true);
      await saveMenuAiDraft(locationId, draftId);
      setStatus('processing');
    } catch (error) {
      ErrorToast('Failed to save the AI menu.');
    } finally {
      setLoading(false);
    }
  };

  // Full wipe — the container calls this only after a FAILED run (post-save
  // cleanup happens in the `saved` socket branch above).
  const reset = () => {
    if (activeDraftIdRef.current) {
      observeMenu(locationId, brandId, false);
    }
    activeDraftIdRef.current = null;
    setDraftId(null);
    setStatus(null);
    setProgress(0);
    setDraftData(null);
    setLoading(false);
  };

  // Called from the "Improve Menu with AI" button BEFORE the modal opens. Wipes
  // local state, then UNCONDITIONALLY deletes the brand's server-side draft —
  // fired every time, with no client-known id, because the draft is shared
  // across admins and no browser can tell whether one exists. Resolves `true`
  // when it's safe to open and `false` on a backend error, in which case the
  // caller keeps the modal closed so the user can retry. Drives the `discarding`
  // loader.
  const discardDraft = async (): Promise<boolean> => {
    if (activeDraftIdRef.current) {
      observeMenu(locationId, brandId, false);
    }
    activeDraftIdRef.current = null;
    setDraftId(null);
    setDraftData(null);
    setProgress(0);
    setStatus(null);

    try {
      setDiscarding(true);
      await deleteMenuAiDraft(locationId, brandId, menuId);
      return true;
    } catch (error) {
      // Surface the backend's message (via handleError) and keep the modal shut.
      reportError(error, {
        context: 'deleteMenuAiDraft',
        fallbackMessage: 'Failed to discard the previous AI draft.',
      });
      return false;
    } finally {
      setDiscarding(false);
    }
  };

  return {
    draftId,
    status,
    progress,
    draftData,
    loading,
    discarding,
    startImprove,
    refineItem,
    undoItem,
    saveDraft,
    reset,
    discardDraft,
  };
};
